<template>
  <v-dialog v-model="dialog" max-width="720px" scrollable>
    <v-card>
      <v-toolbar card color="grey lighten-3">
        <v-toolbar-title>Lịch sử thiết bị</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="history-body">
        <div class="subheading mb-3">
          IMEI: <b>{{ imei }}</b>
        </div>

        <div v-if="isLoading" class="text-xs-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else-if="errorMessage" class="text-xs-center pa-4 error--text">{{ errorMessage }}</div>
        <div v-else-if="entries.length === 0" class="text-xs-center pa-4 grey--text">
          Chưa có lịch sử cho thiết bị này
        </div>

        <div v-else class="flow">
          <template v-for="(node, i) in flow">
            <div v-if="i > 0" :key="'edge-' + i" class="flow-edge">
              <v-icon small>arrow_downward</v-icon>
              <span>{{ node.via }}</span>
            </div>
            <div :key="'node-' + i" class="flow-node" :class="'flow-node--' + node.kind">
              <v-icon class="flow-node__icon">{{ node.icon }}</v-icon>
              <div class="flow-node__body">
                <div class="body-2 font-weight-bold">
                  {{ node.title }}
                  <v-chip v-if="node.count > 1" class="ml-2 my-0" color="indigo" text-color="white" small label>×{{ node.count }}</v-chip>
                </div>
                <div class="caption">{{ node.range }}</div>
                <div v-if="node.by" class="caption">Bởi: {{ node.by }}</div>
                <div v-for="(note, n) in node.notes" :key="'note-' + n" class="caption font-italic">{{ note }}</div>

                <div v-if="node.badges.length" class="mt-1">
                  <v-chip
                    v-for="badge in node.badges"
                    :key="badge.title"
                    :color="isOpen(i, badge) ? 'primary' : ''"
                    :text-color="isOpen(i, badge) ? 'primary' : ''"
                    class="flow-chip"
                    small
                    label
                    outline
                    @click="toggle(i, badge)"
                  >
                    {{ badge.title }}<span v-if="badge.items.length > 1">&nbsp;×{{ badge.items.length }}</span>
                  </v-chip>
                </div>
                <template v-for="badge in node.badges">
                  <div v-if="isOpen(i, badge)" :key="'detail-' + badge.title" class="flow-detail">
                    <div class="caption font-weight-bold">{{ badge.title }}</div>
                    <div v-for="item in badge.items" :key="item.key" class="caption">
                      {{ item.time }} · {{ item.user || 'Không rõ người thao tác' }}<span v-if="item.remark"> — {{ item.remark }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">Đóng</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';

// Event codes come from GET /device/history; unknown ones fall back to the raw code.
const EVENTS = {
  Imported: { title: 'Nhập thiết bị' },
  Moved: { title: 'Chuyển thiết bị' },
  AccountChanged: { title: 'Đổi khách hàng' },
  Updated: { title: 'Cập nhật thông tin' },
  PhoneUpdated: { title: 'Cập nhật SĐT SIM' },
  StatusChanged: { title: 'Đổi trạng thái' },
  PlanCodeChanged: { title: 'Đổi gói cước' },
  Renewed: { title: 'Đồng bộ hạn dịch vụ từ Odoo' },
  LicenseCreated: { title: 'Gia hạn' },
  Registered: { title: 'Đăng ký vào tài khoản' },
  CreatedWithVehicle: { title: 'Tạo xe kèm thiết bị' },
  Installed: { title: 'Gán vào xe' },
  Switched: { title: 'Gán vào xe' },
  SwappedIn: { title: 'Đổi bảo hành (gán vào xe)' },
  SwappedOut: { title: 'Đổi bảo hành (tháo đi bảo trì)' },
  Replaced: { title: 'Bị thay bằng thiết bị khác' },
  Removed: { title: 'Gỡ khỏi xe' }
};

// Events that put the device on a vehicle / take it off. Everything else is either a
// station of its own (import, move, register) or a small marker on the current station.
const ENTER_VEHICLE = ['Installed', 'Switched', 'SwappedIn', 'CreatedWithVehicle'];
const LEAVE_VEHICLE = ['Replaced', 'SwappedOut', 'Removed'];

const formatDate = time => moment(time).format('DD/MM/YYYY');

export default {
  data: () => ({
    dialog: false,
    isLoading: false,
    imei: '',
    entries: [],
    expanded: {},
    errorMessage: '',
    requestId: 0
  }),

  computed: {
    // Stations the device went through, oldest first, each reached via one transition.
    flow () {
      // Oldest first; entries with the same time keep their original (chronological) order.
      const chronological = this.entries
        .map((entry, index) => ({ entry, index }))
        .sort((a, b) => a.entry.time - b.entry.time || b.index - a.index)
        .map(item => item.entry);

      const nodes = [];
      let current = null;

      const open = (kind, title, icon, entry, extraNotes) => {
        const view = this.toViewItem(entry);
        current = {
          kind,
          title,
          icon,
          by: view.user,
          notes: (extraNotes || []).concat(view.remark ? [view.remark] : []),
          from: entry.time,
          to: null,
          via: nodes.length ? `${this.titleOf(entry)} · ${formatDate(entry.time)}` : '',
          range: '',
          count: 1,
          mergeKey: '',
          badges: []
        };
        nodes.push(current);
      };

      const closeVehicle = entry => {
        if (current && current.kind === 'vehicle' && current.to === null) current.to = entry.time;
      };

      chronological.forEach(entry => {
        const related = this.relatedLabel(entry);

        if (entry.event === 'Imported') {
          open('stock', 'Nhập thiết bị', 'inbox', entry);
        } else if (entry.event === 'Moved') {
          // Several moves in a row on the same day by the same person read as one station with a counter.
          const mergeKey = `Moved|${formatDate(entry.time)}|${this.toViewItem(entry).user}`;
          if (current && current.mergeKey === mergeKey) {
            current.count++;
          } else {
            open('move', 'Chuyển thiết bị', 'local_shipping', entry);
            current.mergeKey = mergeKey;
          }
        } else if (entry.event === 'Registered') {
          open('move', 'Đăng ký vào tài khoản', 'person_add', entry);
        } else if (ENTER_VEHICLE.includes(entry.event)) {
          closeVehicle(entry);
          open('vehicle', `Xe ${entry.vehiclePlate || 'không rõ biển số'}`, 'directions_car', entry);
        } else if (LEAVE_VEHICLE.includes(entry.event)) {
          closeVehicle(entry);
          if (entry.event === 'SwappedOut') {
            open('repair', 'Bảo trì (đổi bảo hành)', 'build', entry, related ? [`Thiết bị thay thế: ${related}`] : []);
          } else {
            open('out', 'Chưa gán xe', 'store', entry, entry.event === 'Replaced' && related ? [`Bị thay bằng ${related}`] : []);
          }
        } else {
          // Small marker on whichever station the device is at.
          if (!current) open('start', 'Bắt đầu ghi nhận', 'flag', entry);
          const title = this.titleOf(entry);
          let badge = current.badges.find(b => b.title === title);
          if (!badge) {
            badge = { title, items: [] };
            current.badges.push(badge);
          }
          badge.items.push(this.toViewItem(entry)); // oldest first
        }
      });

      nodes.forEach((node, i) => {
        if (node.kind === 'vehicle') {
          const next = nodes[i + 1];
          const end = node.to !== null ? node.to : (next ? next.from : null);
          node.range = `${formatDate(node.from)} → ${end !== null ? formatDate(end) : 'hiện tại'}`;
        } else {
          node.range = formatDate(node.from);
        }
      });

      return nodes;
    }
  },

  methods: {
    ...mapActions(['getDeviceHistory']),

    open (device) {
      const requestId = ++this.requestId;
      this.imei = device.imei;
      this.entries = [];
      this.expanded = {};
      this.errorMessage = '';
      this.isLoading = true;
      this.dialog = true;

      this.getDeviceHistory({ deviceId: device.id })
        .then(data => {
          if (requestId !== this.requestId) return;
          this.entries = data || [];
        })
        .catch(error => {
          if (requestId !== this.requestId) return;
          this.errorMessage = error === 'ERR_INVALID_PERMISSION'
            ? 'Bạn không có quyền xem lịch sử thiết bị này'
            : 'Không tải được lịch sử thiết bị';
        })
        .then(() => {
          if (requestId === this.requestId) this.isLoading = false;
        });
    },

    close () {
      this.dialog = false;
    },

    badgeKey (nodeIndex, badge) {
      return `${nodeIndex}-${badge.title}`;
    },

    isOpen (nodeIndex, badge) {
      return !!this.expanded[this.badgeKey(nodeIndex, badge)];
    },

    toggle (nodeIndex, badge) {
      const key = this.badgeKey(nodeIndex, badge);
      this.$set(this.expanded, key, !this.expanded[key]);
    },

    titleOf (entry) {
      return (EVENTS[entry.event] || { title: entry.event }).title;
    },

    // "IMEI 8630…" when the other device is known by IMEI, otherwise by id.
    relatedLabel (entry) {
      if (entry.relatedDeviceImei) return `IMEI ${entry.relatedDeviceImei}`;
      return entry.relatedDeviceId ? `thiết bị ${entry.relatedDeviceId}` : '';
    },

    toViewItem (entry, index) {
      return {
        key: `${entry.time}-${index}`,
        time: moment(entry.time).format('DD/MM/YYYY HH:mm:ss'),
        user: entry.userName ? `${entry.userName}${entry.userPhone ? ' - ' + entry.userPhone : ''}` : '',
        remark: entry.remark || ''
      };
    }
  }
};
</script>

<style scoped>
.history-body {
  max-height: 70vh;
}

.flow {
  padding: 4px 0 8px;
}

.flow-node {
  display: flex;
  align-items: flex-start;
  padding: 10px 16px;
  border-left: 4px solid #9e9e9e;
  border-radius: 4px;
  background: #fafafa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.flow-node__icon {
  margin-right: 12px;
  margin-top: 2px;
}

.flow-node__body {
  flex: 1;
}

.flow-node--stock {
  border-left-color: #2196f3;
}

.flow-node--move {
  border-left-color: #3f51b5;
}

.flow-node--vehicle {
  border-left-color: #4caf50;
}

.flow-node--repair {
  border-left-color: #f44336;
}

.flow-node--out {
  border-left-color: #ff9800;
}

.flow-chip {
  cursor: pointer;
}

.flow-detail {
  margin-top: 6px;
  padding: 6px 10px;
  background: #fff;
  border-left: 2px solid #bdbdbd;
}

.flow-edge {
  display: flex;
  align-items: center;
  margin: 4px 0 4px 18px;
  font-size: 12px;
  color: #757575;
}

.flow-edge span {
  margin-left: 4px;
}
</style>



// WEBPACK FOOTER //
// src/pages/device/components/DialogHistory.vue